import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  IconFileTextSpark,
  IconLoader2,
  IconSquareRoundedPlusFilled,
} from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { BackButton } from "../ui/BackButton";
import { useApiQuery, type Api } from "../../services/api";

// The mobile chrome of a conversation (general JuisciGPT, Chat-with-Paper and
// the shared read-only view): an icon-only back, the conversation title in the
// middle, and the conversation-scoped actions on the right. `md:hidden` like
// every other content header — the wide layouts carry their own leading row.

/** The paper whose posters the visual-abstract sheet lists. */
export type ConversationVisualAbstracts = {
  articleId: string;
  roomId: string | null;
  /** The poster is of THIS paper — its title names each row in the sheet. */
  articleTitle: string;
};

export function GptConversationHeader({
  title,
  onBack,
  visualAbstracts,
  newConversationTo,
  actions,
}: {
  /** Centered title. Null while the conversation loads, or before it is named. */
  title: string | null;
  /** What the back button does. Null keeps the browser's own `goBack`. */
  onBack: (() => void) | null;
  /** Null on the surfaces with no grounding paper — the action is not rendered. */
  visualAbstracts: ConversationVisualAbstracts | null;
  /** Null hides the action (an anonymous reader has no chat to start). */
  newConversationTo: string | null;
  /** Trailing surface-specific actions (the owner's conversation menu), or null. */
  actions: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="border-b border-secondary-soft-yellow-grey px-3.5 py-3 md:hidden">
      <div className="relative flex items-center justify-between">
        <BackButton
          onClick={onBack === null ? undefined : onBack}
          className="z-10"
        />
        {title !== null && (
          <span className="absolute left-1/2 max-w-[55%] -translate-x-1/2 truncate text-center font-dm-sans text-sm font-medium text-secondary-bluegrey-7">
            {title}
          </span>
        )}
        <div className="z-10 flex items-center gap-3">
          {visualAbstracts !== null && (
            <VisualAbstractsAction visualAbstracts={visualAbstracts} />
          )}
          {newConversationTo !== null && (
            <Link
              to={newConversationTo}
              aria-label={t("New conversation")}
              className="flex items-center justify-center text-secondary-bluegrey-7 no-underline"
            >
              <IconSquareRoundedPlusFilled className="size-6" />
            </Link>
          )}
          {actions}
        </div>
      </div>
    </div>
  );
}

type VisualAbstractList =
  Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"];

/**
 * The header's visual-abstract opener and the sheet it opens. Renders NOTHING
 * until the paper actually has a poster (or a run in flight): an icon that opens
 * an empty sheet is a dead affordance, and generating one is the transcript
 * card's job, not this sheet's.
 */
function VisualAbstractsAction({
  visualAbstracts,
}: {
  visualAbstracts: ConversationVisualAbstracts;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // Same poll as the transcript card, and the same cache entry: a run started
  // from either surface is reflected in both without a second request.
  const refetchInterval = useCallback(
    (data: VisualAbstractList | undefined) =>
      data !== undefined &&
      data.body.artifacts.some(
        (artifact) =>
          artifact.status === "queued" || artifact.status === "generating"
      )
        ? 3_000
        : false,
    []
  );
  const listQuery = useApiQuery(
    "/webapp/private/articles/:id/visual-abstracts",
    {
      param: { id: visualAbstracts.articleId },
      query: { roomId: visualAbstracts.roomId },
      refetchInterval,
    }
  );
  const artifacts =
    listQuery.state === "success" ? listQuery.body.artifacts : [];
  if (artifacts.length === 0) return null;

  return (
    <>
      <button
        type="button"
        aria-label={t("Visual abstracts")}
        className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-secondary-bluegrey-7"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <IconFileTextSpark className="size-6" stroke={1.5} />
      </button>

      <BottomPanel
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={t("Visual abstracts")}
        displayCloseButton
      >
        <div className="flex flex-col gap-2 p-4">
          {artifacts.map((artifact) => (
            <VisualAbstractRow
              key={artifact.id}
              artifact={artifact}
              articleTitle={visualAbstracts.articleTitle}
              onOpen={() => {
                setIsOpen(false);
              }}
            />
          ))}
        </div>
      </BottomPanel>
    </>
  );
}

/**
 * One poster as a row — the paper it is of, plus what the run is currently
 * doing. Only a settled, ready poster is a link; the others have nothing to
 * open yet.
 */
function VisualAbstractRow({
  artifact,
  articleTitle,
  onOpen,
}: {
  artifact: VisualAbstractList["body"]["artifacts"][number];
  articleTitle: string;
  onOpen: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const rowClassName =
    "flex items-center gap-3 rounded-2xl bg-ds-black/4 p-3 no-underline";
  const icon = (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white">
      <IconFileTextSpark className="size-5 text-ds-moss-600" stroke={1.5} />
    </span>
  );
  const name = (
    <span className="truncate font-dm-sans text-base font-medium text-ds-mint-900">
      {articleTitle}
    </span>
  );

  switch (artifact.status) {
    case "ready":
      return (
        <Link
          to={`/visual-abstract/${artifact.id}/preview`}
          className={rowClassName}
          onClick={onOpen}
        >
          {icon}
          <span className="flex min-w-0 flex-1 flex-col">
            {name}
            <span className="truncate font-dm-sans text-footnote text-ds-slate-600">
              {t("Visual abstract")}
            </span>
          </span>
        </Link>
      );
    case "queued":
    case "generating":
      return (
        <div className={rowClassName}>
          {icon}
          <div className="flex min-w-0 flex-1 flex-col">
            {name}
            <span className="flex items-center gap-1.5 truncate font-dm-sans text-footnote text-ds-slate-600">
              <IconLoader2 className="size-3.5 shrink-0 animate-spin" />
              {t("Generating the visual abstract…")}
            </span>
          </div>
        </div>
      );
    case "failed":
      return (
        <div className={rowClassName}>
          {icon}
          <div className="flex min-w-0 flex-1 flex-col">
            {name}
            <span className="truncate font-dm-sans text-footnote text-secondary-raspberry-1">
              {artifact.failure.message}
            </span>
          </div>
        </div>
      );
    default:
      throw new Error(artifact satisfies never);
  }
}
